/**
 * TalentPoolSearchPage.tsx — the Talent Pool shell.
 *
 * Three views off a thin left rail: New search (NL → /plan) · Results (editable filters + ranked table +
 * profile drawer) · Searches (the recruiter's saved searches). Every run is auto-saved and can be
 * restored or deleted. Criteria are scored per-candidate by apps/server (Gemini 3.1 Flash Lite).
 *
 * The query TREE is the source of truth: count, search and the filter rail all read it directly. The
 * planner's rich AST (temporal `when`, same-entry `scope`, OR/NOT groups) is never flattened away. The
 * "Edit all" modal is the only place we project to/from the flat SearchState — seeded via treeToState and
 * committed via buildTree (both lossless), so the round-trip can't degrade a temporal query.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Sparkles, FolderOpen, Plus, Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import {
    countTree,
    searchTree,
    getScoreStatus,
    planQuery,
    listSavedSearches,
    createSavedSearch,
    updateSavedSearch,
    deleteSavedSearch,
    emptyRoot,
    type CandidateResult,
    type Group,
    type PlanResponse,
    type SavedSearch,
    type ScoredRow,
} from '@/lib/talentPool.api';
import { getApiErrorMessage } from '@/lib/http';
import { SidebarExpandButton } from '@/components/SidebarExpandButton';
import { NewSearch } from './NewSearch';
import { SearchSummary } from './SearchSummary';
import { FilterModal } from './FilterModal';
import { ResultsTable } from './ResultsTable';
import { CandidateProfileDrawer } from './CandidateProfileDrawer';

type View = 'new' | 'results' | 'list';
const runKey = (tree: Group, criteria: string[]) => JSON.stringify({ t: tree, c: criteria });

export default function TalentPoolSearchPage() {
    const [view, setView] = useState<View>('new');

    // ── canonical query state: the tree IS the source of truth (edited directly by the rail + modal) ──
    const [tree, setTree] = useState<Group>(emptyRoot());
    const [criteria, setCriteria] = useState<string[]>([]);
    const [nlQuery, setNlQuery] = useState('');

    // live count
    const [count, setCount] = useState<number | null>(null);
    const [counting, setCounting] = useState(false);
    const countSeq = useRef(0);

    // results
    const [rows, setRows] = useState<CandidateResult[]>([]);
    const [total, setTotal] = useState<number | null>(null);
    const [scored, setScored] = useState(false);
    const [activeCriteria, setActiveCriteria] = useState<string[]>([]);
    const [activeCriteriaLabels, setActiveCriteriaLabels] = useState<string[]>([]);
    const [relaxation, setRelaxation] = useState<string | null>(null);
    const [searching, setSearching] = useState(false);
    const [lastRunKey, setLastRunKey] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [refining, setRefining] = useState(false);

    // progressive (async) criteria scoring — scores stream in from the SQS workers via polling
    const [scoring, setScoring] = useState(false);
    const [scoredCount, setScoredCount] = useState(0);
    const [scoreTotal, setScoreTotal] = useState(0);
    const [scoreIncomplete, setScoreIncomplete] = useState(false); // scoring finished but a chunk was lost
    const scorePollSeq = useRef(0); // bump to cancel an in-flight poll loop (new search / new page)
    const searchSeq = useRef(0); // last-issued search wins — guards doSearch's async writes against overlap

    // table chrome + drawer
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [openId, setOpenId] = useState<string | null>(null); // drawer keyed by candidate id (survives re-rank)
    const [filterOpen, setFilterOpen] = useState(false);

    // saved searches
    const [searches, setSearches] = useState<SavedSearch[]>([]);
    const [searchesLoaded, setSearchesLoaded] = useState(false);
    const [activeSearchId, setActiveSearchId] = useState<string | null>(null);
    const pendingCreate = useRef<Promise<string | null> | null>(null); // in-flight auto-save create

    // ── URL sync: ?search=<savedSearchId>&candidate=<candidateId> so a refresh restores the exact view ──
    const [searchParams, setSearchParams] = useSearchParams();
    const initialParams = useRef({ search: searchParams.get('search'), candidate: searchParams.get('candidate') });
    const restoredRef = useRef(false); // gates the URL writer until we've read the initial URL back out
    const [restoring, setRestoring] = useState(!!initialParams.current.search);

    useEffect(() => {
        listSavedSearches()
            .then(setSearches)
            .catch(() => {})
            .finally(() => setSearchesLoaded(true));
    }, []);

    // cancel any in-flight score poll / search on unmount (no setState-after-unmount, no timer leak)
    useEffect(() => () => {
        scorePollSeq.current++;
        searchSeq.current++;
    }, []);

    // Merge a batch of scored rows into `rows` by candidateId (in place — no reorder while scoring).
    const patchScores = useCallback((results: ScoredRow[]) => {
        if (!results.length) return;
        const byId = new Map(results.map((x) => [x.candidateId, x]));
        setRows((prev) =>
            prev.map((row) => {
                const s = byId.get(String(row.id));
                return s
                    ? {
                          ...row,
                          criteria_results: s.criteria_results,
                          criteria_verdicts: s.criteria_verdicts,
                          criteria_met: s.criteria_met,
                          criteria_total: s.criteria_total,
                          match_rate: s.match_rate,
                          criteria_summary: s.criteria_summary,
                      }
                    : row;
            }),
        );
    }, []);

    // Poll progressive scores for an async run: patch rows by candidateId as they land, re-rank when done.
    const startScorePolling = useCallback(
        (jobId: string, totalToScore: number) => {
            const seq = ++scorePollSeq.current;
            let cursor: string | undefined;
            const startedAt = Date.now();
            setScoring(true);
            setScoredCount(0);
            setScoreTotal(totalToScore);
            setScoreIncomplete(false);

            const finish = async (incomplete: boolean) => {
                if (scorePollSeq.current !== seq) return;
                // final full re-scan (no cursor) so a cross-worker out-of-order _id can't leave a row undelivered
                try {
                    const full = await getScoreStatus(jobId);
                    if (scorePollSeq.current === seq) patchScores(full.results);
                } catch {
                    /* keep whatever we already patched */
                }
                if (scorePollSeq.current !== seq) return;
                setRows((prev) => [...prev].sort((a, b) => (b.match_rate ?? -1) - (a.match_rate ?? -1))); // best rise to top
                setScoreIncomplete(incomplete);
                setScoring(false);
            };

            const tick = async () => {
                if (scorePollSeq.current !== seq) return; // superseded by a newer search
                try {
                    const p = await getScoreStatus(jobId, cursor);
                    if (scorePollSeq.current !== seq) return;
                    patchScores(p.results);
                    if (p.results.length) cursor = p.results[p.results.length - 1].cursor;
                    setScoredCount(p.scored);
                    if (p.status !== 'scoring' || p.scored >= totalToScore) return finish(!!p.incomplete || p.scored < totalToScore);
                } catch {
                    /* transient — keep polling until the safety net */
                }
                if (Date.now() - startedAt > 180000) return finish(true); // 3-min safety net
                setTimeout(tick, 1300);
            };
            tick();
        },
        [patchScores],
    );

    const doSearch = useCallback(
        async (t: Group, crit: string[], q: string): Promise<number | null> => {
            const useScore = crit.length > 0;
            const seq = ++searchSeq.current; // last-issued search wins
            scorePollSeq.current++; // cancel any in-flight score poll from a previous run
            setScoring(false);
            setSearching(true);
            setError(null);
            setRelaxation(null);
            setLastRunKey(runKey(t, crit));
            try {
                const r = await searchTree({ tree: t, page: 1, query: q, criteria: useScore ? crit : undefined, score: useScore });
                if (searchSeq.current !== seq) return r.total ?? 0; // a newer search superseded this one — drop stale writes
                const n = r.total ?? 0;
                setRows(r.results ?? []);
                setTotal(n);
                setActiveCriteria(r.criteria ?? (useScore ? crit : []));
                setActiveCriteriaLabels(r.criteria_labels ?? []);
                setRelaxation(typeof r.relaxation === 'string' ? r.relaxation : null);
                setExpandedId(null);
                setOpenId(null);
                if (r.scoreJobId) {
                    // async scoring: show the criteria columns now (pending cells), stream scores in
                    setScored(true);
                    startScorePolling(r.scoreJobId, r.scoreTotal ?? r.results?.length ?? n);
                } else {
                    setScored(!!r.scored);
                }
                return n;
            } catch (e) {
                setError(getApiErrorMessage(e, 'Search failed.'));
                return null;
            } finally {
                setSearching(false);
            }
        },
        [startScorePolling],
    );

    // persist the active saved search (edits / re-runs). Resolves the id from the in-flight create if the
    // auto-save POST hasn't landed yet, and stores the authoritative search total (not the debounced count).
    const persistActive = useCallback(
        async (t: Group, crit: string[], q: string, lastCount: number | null) => {
            const id = activeSearchId ?? (pendingCreate.current ? await pendingCreate.current : null);
            if (!id) return;
            try {
                const updated = await updateSavedSearch(id, { nlQuery: q, tree: t, criteria: crit, lastCount: lastCount ?? undefined });
                setSearches((prev) => prev.map((s) => (s._id === updated._id ? updated : s)));
            } catch {
                /* ignore */
            }
        },
        [activeSearchId],
    );

    const onPlanned = useCallback(
        async (query: string, plan: PlanResponse) => {
            const planTree = (plan.tree as Group) ?? emptyRoot();
            const planCriteria = plan.criteria ?? [];
            setTree(planTree);
            setCriteria(planCriteria);
            setNlQuery(query);
            // Land on the build/refine view with a live count, but DON'T search yet — the user reviews the
            // filters + criteria and runs the search explicitly. (Reset run state so the Search CTA shows.)
            setRows([]);
            setTotal(null);
            setLastRunKey('');
            setView('results');
            setActiveSearchId(null); // detach from any previously-open saved search until the create resolves
            // auto-save the new search as a draft (uniquify the name on collision so we never silently drop one)
            const base = query.length > 110 ? `${query.slice(0, 110)}…` : query;
            const save = async (name: string, attempt = 0): Promise<string | null> => {
                try {
                    const created = await createSavedSearch({ name, nlQuery: query, tree: planTree, criteria: planCriteria });
                    setSearches((prev) => [created, ...prev]);
                    setActiveSearchId(created._id);
                    return created._id;
                } catch (e: unknown) {
                    const status = (e as { response?: { status?: number } })?.response?.status;
                    if (status === 409 && attempt < 4) return save(`${base} (${attempt + 2})`, attempt + 1);
                    return null;
                }
            };
            pendingCreate.current = save(base);
        },
        [],
    );

    const openSaved = useCallback(
        async (s: SavedSearch) => {
            setTree(s.tree);
            setCriteria(s.criteria);
            setNlQuery(s.nlQuery);
            pendingCreate.current = null;
            setActiveSearchId(s._id);
            setView('results');
            await doSearch(s.tree, s.criteria, s.nlQuery);
        },
        [doSearch],
    );

    const removeSaved = useCallback(
        async (id: string) => {
            try {
                await deleteSavedSearch(id);
                setSearches((prev) => prev.filter((s) => s._id !== id));
                if (activeSearchId === id) setActiveSearchId(null);
            } catch {
                /* ignore */
            }
        },
        [activeSearchId],
    );

    const newSearch = useCallback(() => {
        scorePollSeq.current++; // cancel any running score poll
        searchSeq.current++; // supersede any in-flight search
        setScoring(false);
        setScoreIncomplete(false);
        setOpenId(null);
        setTree(emptyRoot());
        setCriteria([]);
        setNlQuery('');
        setRows([]);
        setTotal(null);
        setCount(null);
        setScored(false);
        setActiveCriteria([]);
        setActiveCriteriaLabels([]);
        setRelaxation(null);
        setLastRunKey('');
        setActiveSearchId(null);
        pendingCreate.current = null;
        setView('new');
    }, []);

    // Live count keys off the canonical tree — the rail and the modal both edit it directly.
    const treeKey = useMemo(() => JSON.stringify(tree), [tree]);
    useEffect(() => {
        if (view !== 'results') return;
        const seq = ++countSeq.current;
        setCounting(true);
        const t = setTimeout(async () => {
            try {
                const { total: n } = await countTree(JSON.parse(treeKey));
                if (countSeq.current === seq) setCount(n);
            } catch {
                if (countSeq.current === seq) setCount(null);
            } finally {
                if (countSeq.current === seq) setCounting(false);
            }
        }, 350);
        return () => clearTimeout(t);
    }, [treeKey, view]);

    const searched = lastRunKey !== '';
    const dirty = view === 'results' && searched && runKey(tree, criteria) !== lastRunKey;
    // drawer index derived from the candidate id, so a re-rank never points the drawer at the wrong person
    const openIdx = openId != null ? rows.findIndex((r) => r.id === openId) : -1;

    // Edit-all modal edits the canonical tree directly (no bridge); Search commits by running, close just hides.
    const applyAndSearch = useCallback(async () => {
        setFilterOpen(false);
        const n = await doSearch(tree, criteria, nlQuery);
        persistActive(tree, criteria, nlQuery, n);
    }, [tree, criteria, nlQuery, doSearch, persistActive]);

    const onUpdate = useCallback(async () => {
        const n = await doSearch(tree, criteria, nlQuery);
        persistActive(tree, criteria, nlQuery, n);
    }, [tree, criteria, nlQuery, doSearch, persistActive]);

    // AI refine: an NL instruction edits the CURRENT tree + criteria in place (doesn't auto-search).
    const onRefine = useCallback(
        async (instruction: string) => {
            const q = instruction.trim();
            if (!q) return;
            setRefining(true);
            setError(null);
            try {
                const plan = await planQuery(q, { tree, criteria });
                if (plan.tree) setTree(plan.tree as Group);
                if (Array.isArray(plan.criteria)) setCriteria(plan.criteria);
            } catch (e) {
                setError(getApiErrorMessage(e, 'Could not update with AI.'));
            } finally {
                setRefining(false);
            }
        },
        [tree, criteria],
    );

    const toggleExpand = useCallback((id: string) => setExpandedId((prev) => (prev === id ? null : id)), []);

    // Restore ?search / ?candidate from the URL once the saved-search list is available (refresh-safe).
    // openSaved re-runs the search; we then re-open the drawer candidate (it lands in the fetched page).
    useEffect(() => {
        if (restoredRef.current) return;
        const sid = initialParams.current.search;
        if (!sid) {
            restoredRef.current = true; // nothing to restore — enable the URL writer
            return;
        }
        const s = searches.find((x) => x._id === sid);
        if (!s) {
            if (searchesLoaded) {
                restoredRef.current = true; // id not found (deleted / different user) — stop waiting
                setRestoring(false);
            }
            return; // else keep waiting for the saved-search list to load
        }
        restoredRef.current = true;
        (async () => {
            try {
                await openSaved(s);
                if (initialParams.current.candidate) setOpenId(initialParams.current.candidate);
            } finally {
                setRestoring(false);
            }
        })();
    }, [searches, searchesLoaded, openSaved]);

    // Mirror the active search + open candidate into the URL (replace, so it never spams history).
    useEffect(() => {
        if (!restoredRef.current) return; // don't clobber the initial URL before we've restored from it
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                if (activeSearchId) next.set('search', activeSearchId);
                else next.delete('search');
                if (activeSearchId && openId) next.set('candidate', openId);
                else next.delete('candidate');
                return next;
            },
            { replace: true },
        );
    }, [activeSearchId, openId, setSearchParams]);

    return (
        <div className="flex h-full flex-col bg-zinc-50 text-zinc-900">
            {/* top nav bar — kept off the far-left edge so the global sidebar's collapse/peek never overlaps it */}
            <header className="flex h-12 shrink-0 items-center gap-1 border-b border-zinc-200 bg-white px-3">
                <SidebarExpandButton style={{ marginRight: 2 }} />
                <NavBtn label="New search" icon={Plus} active={view === 'new'} onClick={newSearch} />
                <NavBtn label="Searches" icon={FolderOpen} active={view === 'list'} onClick={() => setView('list')} />
            </header>

            <main className="flex min-h-0 min-w-0 flex-1 flex-col">
                {restoring && (
                    <div className="flex flex-1 items-center justify-center gap-2 text-zinc-400">
                        <Loader2 size={18} className="animate-spin" />
                        <span className="text-[13px]">Restoring your search…</span>
                    </div>
                )}

                {!restoring && view === 'new' && <NewSearch onPlanned={onPlanned} />}

                {!restoring && view === 'list' && <SavedList searches={searches} onOpen={openSaved} onDelete={removeSaved} onNew={newSearch} />}

                {!restoring && view === 'results' && (
                    <div className="flex min-h-0 flex-1">
                        <SearchSummary
                            tree={tree}
                            onTreeChange={setTree}
                            criteria={criteria}
                            onCriteriaChange={setCriteria}
                            count={count}
                            counting={counting}
                            searched={searched}
                            dirty={dirty}
                            searching={searching}
                            refining={refining}
                            onRefine={onRefine}
                            onEditAll={() => setFilterOpen(true)}
                            onRun={onUpdate}
                        />
                        <section className="flex min-h-0 min-w-0 flex-1 flex-col p-4">
                            <div className="mb-3 flex items-center gap-2">
                                <h2 className="text-[14px] font-semibold text-zinc-900">
                                    Results {total != null && <span className="font-normal text-zinc-400">· {total.toLocaleString()} total</span>}
                                </h2>
                                {rows.length > 0 && <span className="text-[12px] text-zinc-400">(Showing {rows.length} results)</span>}
                                {searching && <Loader2 size={14} className="animate-spin text-zinc-400" />}
                                {scoring && (
                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FD775E]/10 px-2.5 py-0.5 text-[12px] font-medium text-[#c2452c]">
                                        <Loader2 size={12} className="animate-spin" /> Scoring {scoredCount}/{scoreTotal}…
                                    </span>
                                )}
                            </div>
                            {error && <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">{error}</div>}
                            {scoreIncomplete && !scoring && (
                                <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] leading-relaxed text-amber-800">
                                    <AlertTriangle size={14} className="mt-px shrink-0" />
                                    <span>Some candidates couldn't be scored (a scoring worker didn't finish). Re-run the search to score them.</span>
                                </div>
                            )}
                            {relaxation && (
                                <div className="mb-3 flex items-start gap-2 rounded-lg border border-[#0D1830]/30 bg-[#0D1830]/5 px-3 py-2 text-[12px] leading-relaxed text-zinc-700">
                                    <AlertTriangle size={14} className="mt-px shrink-0 text-[#0D1830]" />
                                    <span>{relaxation}</span>
                                </div>
                            )}
                            <div className="min-h-0 flex-1 overflow-y-auto">
                                {searched ? (
                                    <ResultsTable
                                        rows={rows}
                                        total={total ?? rows.length}
                                        criteria={activeCriteria}
                                        criteriaLabels={activeCriteriaLabels}
                                        scored={scored}
                                        scoring={scoring}
                                        expandedId={expandedId}
                                        onToggleExpand={toggleExpand}
                                        onOpen={(c) => setOpenId(c.id)}
                                    />
                                ) : (
                                    <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
                                        <p className="max-w-[360px] text-[13px] leading-relaxed text-zinc-500">
                                            Review and refine the filters and must-have criteria on the left
                                            {count != null && (
                                                <>
                                                    {' '}— <span className="font-semibold text-zinc-700">{count.toLocaleString()}</span> candidates match so far
                                                </>
                                            )}
                                            . Run the search when you're ready.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={onUpdate}
                                            disabled={searching || counting}
                                            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0D1830] px-5 py-2.5 text-[13px] font-medium text-white hover:bg-[#1C2B4D] disabled:opacity-50"
                                        >
                                            {searching ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
                                            Search candidates
                                        </button>
                                    </div>
                                )}
                            </div>
                        </section>
                    </div>
                )}
            </main>

            {filterOpen && (
                <FilterModal
                    tree={tree}
                    onChange={setTree}
                    count={count}
                    counting={counting}
                    searching={searching}
                    onClose={() => setFilterOpen(false)}
                    onSearch={applyAndSearch}
                />
            )}

            {openIdx >= 0 && rows[openIdx] && (
                <CandidateProfileDrawer
                    summary={rows[openIdx]}
                    onClose={() => setOpenId(null)}
                    criteria={activeCriteria}
                    criteriaLabels={activeCriteriaLabels}
                    onPrev={() => setOpenId(rows[openIdx - 1]?.id ?? openId)}
                    onNext={() => setOpenId(rows[openIdx + 1]?.id ?? openId)}
                    isFirst={openIdx === 0}
                    isLast={openIdx === rows.length - 1}
                    index={openIdx}
                    total={rows.length}
                />
            )}
        </div>
    );
}

function NavBtn({ label, icon: Icon, active, onClick }: { label: string; icon: typeof Search; active: boolean; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[13px] font-medium transition-colors ${
                active ? 'bg-[#0D1830]/10 text-[#0D1830]' : 'text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800'
            }`}
        >
            <Icon size={16} /> {label}
        </button>
    );
}

function SavedList({
    searches,
    onOpen,
    onDelete,
    onNew,
}: {
    searches: SavedSearch[];
    onOpen: (s: SavedSearch) => void;
    onDelete: (id: string) => void;
    onNew: () => void;
}) {
    return (
        <div className="mx-auto w-full max-w-[760px] flex-1 overflow-y-auto p-8">
            <div className="mb-4 flex items-center justify-between">
                <h1 className="text-[20px] font-semibold text-zinc-900">Your searches</h1>
                <button type="button" onClick={onNew} className="inline-flex items-center gap-1.5 rounded-lg bg-[#0D1830] px-3 py-2 text-[13px] font-medium text-white hover:bg-[#1C2B4D]">
                    <Plus size={15} /> New search
                </button>
            </div>
            {searches.length === 0 ? (
                <div className="rounded-xl border border-dashed border-zinc-200 bg-white p-10 text-center text-[13px] text-zinc-500">
                    No saved searches yet.{' '}
                    <button type="button" onClick={onNew} className="font-medium text-[#0D1830] hover:underline">
                        Start a new search
                    </button>
                    .
                </div>
            ) : (
                <div className="flex flex-col gap-2">
                    {searches.map((s) => {
                        const filters = s.tree?.children?.length ?? 0;
                        return (
                            <div
                                key={s._id}
                                className="group flex items-center gap-3 rounded-xl border border-zinc-200 bg-white px-4 py-3 transition-colors hover:border-[#0D1830]"
                            >
                                <button type="button" onClick={() => onOpen(s)} className="min-w-0 flex-1 text-left">
                                    <div className="line-clamp-1 text-[14px] font-medium text-zinc-900">{s.name}</div>
                                    <div className="mt-0.5 text-[12px] text-zinc-500">
                                        {filters} filter{filters === 1 ? '' : 's'}
                                        {s.criteria.length ? ` · ${s.criteria.length} criteria` : ''}
                                        {s.lastCount != null ? ` · ${s.lastCount.toLocaleString()} matched` : ''}
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onDelete(s._id)}
                                    className="shrink-0 rounded-md p-1.5 text-zinc-300 opacity-0 transition hover:bg-zinc-100 hover:text-red-500 group-hover:opacity-100"
                                    aria-label="Delete saved search"
                                >
                                    <Trash2 size={15} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
